import React, { useMemo, useState } from 'react';
import { AlertTriangle, FileText, Info } from 'lucide-react';

interface MinhaMesaInstructionsProps {
  instruction: string | null;
  /** Layout amplo para guia de execução na bancada */
  variant?: 'default' | 'wide';
}

const IMAGE_URL_REGEX = /^(https?:\/\/\S+\.(?:png|jpe?g|gif|webp|svg))$/i;
const MARKDOWN_IMAGE_REGEX = /!\[[^\]]*\]\((https?:\/\/[^\s)]+\.(?:png|jpe?g|gif|webp|svg))\)/i;
const SECTION_HEADER_REGEX = /^(?:#{1,3}\s+)?([A-ZÁÀÂÃÉÊÍÓÔÕÚÇ][\wÀ-ú\s]{1,28})(?:\s*:)?\s*$/;
const ACTION_VERB_REGEX = /^(Cadastrar|Vincular|Definir|Preencher|Verificar|Conferir|Selecionar|Inserir|Registrar|Confirmar|Checar|Abrir|Fechar|Salvar|Enviar|Criar|Atualizar|Lançar|Validar|Informar|Assinar|Anexar|Revisar|Acionar|Marcar|Descrever|Identificar|Calcular|Aprovar|Liberar|Realizar|Acessar|Digitalizar|Imprimir|Comunicar|Notificar|Classificar|Solicitar|Executar|Pagar|Cobrar|Encaminhar|Retornar)\b/;

type InstructionSection = {
  title: string;
  blocks: string[];
};

function renderInlineWithVerbBold(text: string, keyPrefix: string) {
  const verbMatch = text.match(ACTION_VERB_REGEX);
  if (verbMatch) {
    const verb = verbMatch[0];
    const rest = text.slice(verb.length);
    return (
      <>
        <strong key={`${keyPrefix}-verb`} className="font-semibold text-slate-900 dark:text-white">{verb}</strong>
        {renderInlineHighlights(rest, keyPrefix)}
      </>
    );
  }
  return renderInlineHighlights(text, keyPrefix);
}

function renderInlineHighlights(text: string, keyPrefix: string) {
  return text.split(/(\*\*.*?\*\*)/g).map((chunk, index) => {
    if (chunk.startsWith('**') && chunk.endsWith('**')) {
      return (
        <strong key={`${keyPrefix}-${index}`} className="font-semibold text-slate-900 dark:text-white">
          {chunk.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={`${keyPrefix}-${index}`}>{chunk}</React.Fragment>;
  });
}

function parseSections(instruction: string): InstructionSection[] {
  const blocks = instruction
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);

  const sections: InstructionSection[] = [];
  let current: InstructionSection | null = null;

  for (const block of blocks) {
    const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
    const isSingleLineHeader =
      lines.length === 1 && SECTION_HEADER_REGEX.test(lines[0]) && !/^[-*•\d]/.test(lines[0]);

    if (isSingleLineHeader) {
      const title = lines[0].replace(/^#{1,3}\s+/, '').replace(/:$/, '').trim();
      current = { title, blocks: [] };
      sections.push(current);
      continue;
    }

    if (!current) {
      current = { title: 'Execução', blocks: [] };
      sections.push(current);
    }
    current.blocks.push(block);
  }

  return sections;
}

export const MinhaMesaInstructions: React.FC<MinhaMesaInstructionsProps> = ({
  instruction,
  variant = 'default',
}) => {
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const isWide = variant === 'wide';

  const toggle = (key: string) =>
    setChecked((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  const sections = useMemo(
    () => (instruction && isWide ? parseSections(instruction) : []),
    [instruction, isWide],
  );

  if (!instruction) {
    return (
      <p className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
        Nenhuma instrução SOP definida para esta etapa.
      </p>
    );
  }

  const renderBlock = (block: string, index: number, keyPrefix: string) => {
    const markdownImage = block.match(MARKDOWN_IMAGE_REGEX)?.[1] ?? null;
    const imageUrl = markdownImage ?? (IMAGE_URL_REGEX.test(block) ? block : null);

    if (imageUrl) {
      return (
        <img
          key={`${imageUrl}-${index}`}
          src={imageUrl}
          alt={`Instrução ${index + 1}`}
          className="max-h-[280px] w-full rounded-sm border border-slate-200 object-contain dark:border-slate-700"
        />
      );
    }

    const calloutMatch = block.match(/^(aten[cç][aã]o|alerta|obs|nota)\s*:\s*(.*)$/i);
    if (calloutMatch) {
      const kind = calloutMatch[1].toLowerCase();
      const content = calloutMatch[2];
      const isAlert = kind.includes('aten') || kind.includes('alerta');

      return (
        <div
          key={`${block}-${index}`}
          className={`rounded-sm border px-3 py-2.5 ${isAlert ? 'border-amber-500/40 bg-amber-500/10' : 'border-blue-500/30 bg-blue-500/10'}`}
        >
          <div className="flex items-start gap-2">
            {isAlert ? (
              <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-500" />
            ) : (
              <Info size={14} className="mt-0.5 shrink-0 text-blue-500" />
            )}
            <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-slate-700 dark:text-slate-300">
              {renderInlineHighlights(content, `callout-${index}`)}
            </p>
          </div>
        </div>
      );
    }

    const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
    const isOrderedList = lines.every((line) => /^\d+\.\s+/.test(line));
    const isBulletList = lines.every((line) => /^[-*•]\s+/.test(line));

    if (isOrderedList || isBulletList) {
      const totalItems = lines.length;
      const checkedCount = lines.filter((_, li) =>
        checked.has(`${keyPrefix}-${isOrderedList ? '' : 'b'}${index}-${li}`),
      ).length;

      return (
        <div key={`${block}-${index}`} className="space-y-2">
          {checkedCount > 0 && (
            <p className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
              {checkedCount}/{totalItems} concluídos
            </p>
          )}
          <ul className={isWide ? 'grid gap-2 sm:grid-cols-2' : 'space-y-1.5'}>
            {lines.map((line, lineIndex) => {
              const content = line.replace(/^\d+\.\s+/, '').replace(/^[-*•]\s+/, '');
              const itemKey = `${keyPrefix}-${isOrderedList ? '' : 'b'}${index}-${lineIndex}`;
              const isItemChecked = checked.has(itemKey);
              return (
                <li
                  key={`${line}-${lineIndex}`}
                  className={`flex items-start gap-3 rounded-sm border px-3 py-2.5 transition-colors ${
                    isItemChecked
                      ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-800/40 dark:bg-emerald-950/20'
                      : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900/30'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isItemChecked}
                    onChange={() => toggle(itemKey)}
                    className="mm-sop-checkbox"
                    aria-label={content.slice(0, 80)}
                  />
                  <span
                    className={`text-[13px] leading-relaxed text-slate-700 dark:text-slate-300 ${
                      isItemChecked ? 'line-through opacity-50' : ''
                    }`}
                  >
                    {renderInlineWithVerbBold(content, `${keyPrefix}-${index}-${lineIndex}`)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      );
    }

    const paraLines = block.split('\n').map((l) => l.trim()).filter(Boolean);
    return (
      <div key={`${block}-${index}`} className={isWide ? 'grid gap-2 sm:grid-cols-2' : 'space-y-1.5'}>
        {paraLines.map((line, li) => {
          const itemKey = `${keyPrefix}-p-${index}-${li}`;
          const isItemChecked = checked.has(itemKey);
          return (
            <label
              key={`${block}-${index}-${li}`}
              className={`flex cursor-pointer items-start gap-3 rounded-sm border px-3 py-2.5 transition-colors ${
                isItemChecked
                  ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-800/40 dark:bg-emerald-950/20'
                  : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900/30'
              }`}
            >
              <input
                type="checkbox"
                checked={isItemChecked}
                onChange={() => toggle(itemKey)}
                className="mm-sop-checkbox"
              />
              <span
                className={`text-[13px] leading-relaxed text-slate-700 dark:text-slate-300 ${
                  isItemChecked ? 'line-through opacity-50' : ''
                }`}
              >
                {renderInlineWithVerbBold(line, `para-${index}-${li}`)}
              </span>
            </label>
          );
        })}
      </div>
    );
  };

  if (isWide) {
    return (
      <div className="mm-work-font space-y-5">
        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
          <FileText size={14} />
          <span className="text-[10px] font-bold uppercase tracking-[0.14em]">Guia de Execução · SOP</span>
        </div>
        <div className="space-y-6">
          {sections.map((section) => (
            <section key={section.title} className="space-y-3">
              <h4 className="border-b border-slate-200 pb-2 text-[15px] font-semibold tracking-tight text-slate-800 dark:border-slate-700 dark:text-slate-100">
                {section.title}
              </h4>
              <div className="space-y-3">
                {section.blocks.map((block, index) => renderBlock(block, index, section.title))}
              </div>
            </section>
          ))}
        </div>
      </div>
    );
  }

  const blocks = instruction
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
        <FileText size={13} />
        <span className="font-mono text-[9px] font-bold uppercase tracking-[0.14em]">SOP · Como executar</span>
      </div>
      <div className="space-y-3 text-[12px] leading-relaxed text-slate-700 dark:text-slate-300">
        {blocks.map((block, index) => renderBlock(block, index, 'default'))}
      </div>
    </div>
  );
};
